import { useEffect, useRef, type RefObject } from 'react';
import { socketService } from '@/services/SocketService';
import { usePCMAudioStream } from '@/hooks/usePCMAudioStream';
import type { TextMessage } from '@/components/chat/MessageBubble';
import type { ConfirmMessage } from '@/components/chat/ConfirmCard';
import type { ActionCardMessage } from '@/components/chat/ActionCard';
import type { CardsMessage } from '@/components/chat/UXView';
import type { DraftCardMessage } from '@/components/Communications/ComposeCard';
import type { NoteCardMessage } from '@/components/chat/NotePanel';
import type { WorkflowCardMessage } from '@/components/Workflow/WorkflowPanel';

type ChatMessage = TextMessage | CardsMessage | ConfirmMessage | ActionCardMessage | DraftCardMessage | NoteCardMessage | WorkflowCardMessage;

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

export function useAgentSocket(opts: {
  audioOut: ReturnType<typeof usePCMAudioStream>;
  playbackRef: RefObject<boolean>;
  realtimeAsstIdRef: RefObject<string>;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  setConversationId: (id: string) => void;
  setLiveCaption: (text: string) => void;
  onFirstResponse?: () => void;
}): void {
  const { audioOut, playbackRef, realtimeAsstIdRef, setMessages, setConversationId, setLiveCaption, onFirstResponse } = opts;

  // Stable refs so the effect registers socket handlers exactly once ([] deps) while always
  // calling the latest version of each callback — avoids constant cleanup/re-register on every
  // ChatPage re-render.
  const audioOutRef = useRef(audioOut);
  audioOutRef.current = audioOut;
  const setMessagesRef = useRef(setMessages);
  setMessagesRef.current = setMessages;
  const setConversationIdRef = useRef(setConversationId);
  setConversationIdRef.current = setConversationId;
  const setLiveCaptionRef = useRef(setLiveCaption);
  setLiveCaptionRef.current = setLiveCaption;
  const onFirstResponseRef = useRef(onFirstResponse);
  onFirstResponseRef.current = onFirstResponse;

  useEffect(() => {
    const onChunkEvt = (raw: unknown) => {
      const d = raw as { message_id: string; text: string; conversation_id?: string };
      if (!d?.message_id) return;
      if (d.conversation_id) setConversationIdRef.current(d.conversation_id);
      onFirstResponseRef.current?.();
      setMessagesRef.current(prev => {
        const i = prev.findIndex(m => m.id === d.message_id);
        if (i === -1) return [...prev, { kind: 'text', id: d.message_id, role: 'assistant', text: d.text || '', streaming: true, createdAt: Date.now() } as TextMessage];
        const c = [...prev];
        const existing = c[i];
        if (existing.kind === 'text') c[i] = { ...existing, text: existing.text + (d.text || '') };
        return c;
      });
    };

    const onDone = (raw: unknown) => {
      const d = raw as { message_id: string; conversation_id?: string };
      if (d?.conversation_id) setConversationIdRef.current(d.conversation_id);
      onFirstResponseRef.current?.();
      setMessagesRef.current(prev => prev.map(m => (m.kind === 'text' && m.id === d?.message_id ? { ...m, streaming: false } : m)));
    };

    const onTranscript = (raw: unknown) => {
      if (!playbackRef.current) return;
      const d = raw as { text: string; final: boolean; role: string; turn_id?: string; run_id?: string };
      if (!d?.text) return;
      if (d.role === 'user') {
        setLiveCaptionRef.current(d.final ? '' : d.text);
        if (d.final) {
          // The user's STT (Whisper) often completes AFTER the assistant has already started its
          // spoken reply, so a naive append would render the user line BELOW the reply. Slot it
          // before the assistant bubble only while that bubble is still STREAMING — that means it's
          // the live reply to this turn; a finalized bubble belongs to a prior turn, so append.
          setMessagesRef.current(prev => {
            const userMsg = { kind: 'text', id: `u-${Date.now()}`, role: 'user', text: d.text, createdAt: Date.now() } as TextMessage;
            const aid = realtimeAsstIdRef.current;
            const ai = aid ? prev.findIndex(m => m.id === aid) : -1;
            const streaming = ai !== -1 && prev[ai].kind === 'text' && (prev[ai] as TextMessage).streaming;
            if (!streaming) return [...prev, userMsg];
            return [...prev.slice(0, ai), userMsg, ...prev.slice(ai)];
          });
        }
        return;
      }
      if (d.role === 'assistant') {
        // Key the bubble by the response's turn_id so every delta + the final (and a repeated
        // `*_transcript.done`) collapse into ONE bubble, and a new response always starts a fresh
        // one — no dependence on status/event ordering. Fallback (no turn_id from an older backend):
        // a per-turn ref that resets on `final`.
        const id = d.turn_id
          ? `a-rt-${d.turn_id}`
          : (realtimeAsstIdRef.current || `a-rt-${Date.now()}`);
        setMessagesRef.current(prev => {
          const i = prev.findIndex(m => m.id === id);
          const prevText = i >= 0 && prev[i].kind === 'text' ? (prev[i] as TextMessage).text : '';
          const text = d.final ? d.text : prevText + d.text;
          // runId ties this bubble to its session's conversation_runs doc (one per start→stop).
          if (i === -1) return [...prev, { kind: 'text', id, role: 'assistant', text, streaming: !d.final, voice: true, runId: d.run_id, createdAt: Date.now() } as TextMessage];
          const c = [...prev];
          if (c[i].kind === 'text') c[i] = { ...(c[i] as TextMessage), text, streaming: !d.final, voice: true, runId: d.run_id ?? (c[i] as TextMessage).runId };
          return c;
        });
        // Remember this bubble so a late user transcript can anchor before it. Fallback path resets
        // on `final` so the next turn (which has no new turn_id to distinguish it) starts fresh.
        realtimeAsstIdRef.current = (!d.turn_id && d.final) ? '' : id;
        return;
      }
    };

    // Barge-in: the user interrupted SAM mid-speech. Flush the buffered TTS immediately (otherwise
    // SAM keeps talking over the user) and reset the assistant turn so the next reply starts fresh.
    const onAudioInterrupted = () => {
      audioOutRef.current.stop();
      realtimeAsstIdRef.current = '';
      setLiveCaptionRef.current('');
    };

    const onPttAudio = (raw: unknown) => {
      if (!playbackRef.current) return;
      if (Array.isArray(raw) && raw[0]) audioOutRef.current.enqueue(raw[0] as ArrayBuffer);
    };

    const onRealtimeAudio = (raw: unknown) => {
      if (!playbackRef.current) return;
      const d = raw as { data?: string };
      if (d?.data) audioOutRef.current.enqueue(base64ToArrayBuffer(d.data));
    };

    const onError = (raw: unknown) => {
      const d = raw as { message?: string };
      setMessagesRef.current(prev => [...prev, {
        kind: 'text', id: `err-${Date.now()}`, role: 'assistant', createdAt: Date.now(),
        text: d?.message || 'Something went wrong. Please try again.',
      } as TextMessage]);
    };

    socketService.on('agent:response', onChunkEvt);
    socketService.on('agent:response_done', onDone);
    socketService.on('agent:transcript', onTranscript);
    socketService.on('agent:audio', onPttAudio);
    socketService.on('agent:error', onError);
    socketService.on('agent_realtime:transcript', onTranscript);
    socketService.on('agent_realtime:audio', onRealtimeAudio);
    socketService.on('agent_realtime:audio_interrupted', onAudioInterrupted);

    return () => {
      socketService.off('agent:response', onChunkEvt);
      socketService.off('agent:response_done', onDone);
      socketService.off('agent:transcript', onTranscript);
      socketService.off('agent:audio', onPttAudio);
      socketService.off('agent:error', onError);
      socketService.off('agent_realtime:transcript', onTranscript);
      socketService.off('agent_realtime:audio', onRealtimeAudio);
      socketService.off('agent_realtime:audio_interrupted', onAudioInterrupted);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
